import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * Who paid the entry fees, as a sub-division of the ONE `entry_fees_consumed`
 * edge — never a replacement for it. The edge keeps its own amount, so the
 * graph the residual is checked against is untouched by this field existing.
 *
 * Segments carry the edge's sign, and the three of them sum to it exactly.
 * They are a sub-division of the SELECTED cohort, not of the whole ledger: on a
 * single-cohort filter the other two are structurally `"0.00"` because those
 * payers are outside the diagram, not because they paid nothing.
 *
 * All three are always served, `"0.00"` included — same reason as
 * `residualUsdt`: a zero must be an assertion, not a dropped field.
 */
export const ArenaOpsEntryFeePayerSchema = z.object({
  key: z.enum(["real", "bots", "staff"]),
  label: z.string(),
  amountUsdt: ArenaOpsUsdtSchema,
});
export type ArenaOpsEntryFeePayer = z.infer<typeof ArenaOpsEntryFeePayerSchema>;

/**
 * P6 — where the money went, as a flow graph. Link amounts are SIGNED: outflows are
 * negated, so an outflow that moved money the wrong way arrives negative and stays
 * visible. Take the absolute value for geometry only, never for the figure shown —
 * `residualUsdt` is signed for the same reason.
 *
 * `residualUsdt` is ALWAYS served, including when it is `"0.00"`. Closure is
 * structural — every edge is one measured aggregate and the deltas in a range
 * telescope to `closing − opening` — so a zero residual is the assertion that
 * the graph closed, not the absence of a complaint. Modelling it as a node that
 * appears only on failure would make "the graph closed" indistinguishable from
 * "the residual went missing", and would silently disarm the tripwire the whole
 * edge list is built around. The node still appears only when it is non-zero;
 * that is the visual treatment, not the source of truth.
 */
export const ArenaOpsMoneySankeyResponseSchema = z.object({
  nodes: z.array(z.object({ id: z.string(), label: z.string() })),
  links: z.array(z.object({ source: z.string(), target: z.string(), amountUsdt: ArenaOpsUsdtSchema })),
  residualUsdt: ArenaOpsUsdtSchema,
  /** `nodeId` names the edge these segments divide, so no consumer holds a second copy of that association. */
  entryFeesByPayer: z.object({ nodeId: z.string(), segments: z.array(ArenaOpsEntryFeePayerSchema) }),
});
export type ArenaOpsMoneySankeyResponse = z.infer<typeof ArenaOpsMoneySankeyResponseSchema>;
